import { Trans, useLingui } from "@lingui/react/macro";
import { useQuery } from "@tanstack/react-query";
import { observable } from "mobx";
import { observer } from "mobx-react-lite";
import { useEffect, useRef, useState } from "react";

import {
  ClinicalStudies,
  ClinicalStudyEnrollmentResponse,
  ClinicalStudyVisit_Input,
  type CreateClinicalStudyVisit,
  type CreateStudyEnrollmentSchema,
  ProposedStudyVisitSchedule,
  apiDeleteClinicalStudiesEnrollmentUuid,
  apiGetClinicalStudiesOptions,
  apiGetClinicalStudiesQueryKey,
  apiPostClinicalStudiesEnroll,
  apiPostClinicalStudiesStudyVisit,
  apiPutClinicalStudiesEnrollmentUuid,
  apiPutClinicalStudiesStudyVisitUuid,
} from "../../../api";
import { queryClient } from "../../../api/query-client";
import { AppModel } from "../../../app/app-model";
import { Button } from "../../../components/ui-kit/button";
import {
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
} from "../../../components/ui-kit/dialog";
import { useDialog } from "../../../components/ui-kit/dialog-provider";
import { Form } from "../../../components/ui-kit/form/form";
import { Spinner } from "../../../components/ui-kit/spinner";
import { getServerTodayISO } from "../../../utils/server-time";
import {
  compareDateStr,
  dateTimeISOString,
  dateTimeStrToDateStr,
  requireNonNull,
} from "../../../utils/utils";
import { ClinicalStudyEnrollmentFormFields } from "../../clinical-studies/enrollment-form-fields";
import { fetchEnrollmentPrefill } from "../../clinical-studies/utils";

interface VisitData {
  uuid?: string;
  visit_date: string;
  visit_type: string;
}

type EnrollFormState = {
  patient_id: number;
  enrollment_date: string;
  study_acronym: ClinicalStudies | undefined;
  comment: string | null | undefined;
};

function toEnrollPayload(
  formState: EnrollFormState,
  studyAcronym: ClinicalStudies,
  prefillSchedule: ProposedStudyVisitSchedule[],
  visits: VisitData[],
): CreateStudyEnrollmentSchema {
  return {
    patient_id: formState.patient_id,
    enrollment_date: formState.enrollment_date,
    study_acronym: studyAcronym,
    comment: formState.comment,
    // Backend accepts the string form of visit_type even though the
    // generated input type is an object (asymmetric Pydantic models).
    schedule: prefillSchedule.map((s) => ({
      ...s,
      visit_type: s.visit_type as unknown as ClinicalStudyVisit_Input,
      scheduled_date:
        visits.find((v) => v.visit_type === s.visit_type)?.visit_date ??
        s.corrected_date ??
        s.exact_date,
    })),
  };
}

function toVisitPayload(
  visit: VisitData,
  studyAcronym: ClinicalStudies,
  enrollmentUuid: string,
  patientId: number,
): CreateClinicalStudyVisit {
  return {
    patient_id: patientId,
    study_acronym: studyAcronym,
    study_enrollment_uuid: enrollmentUuid,
    visit_type: visit.visit_type as unknown as ClinicalStudyVisit_Input,
    visit_datetime: dateTimeISOString(visit.visit_date),
  };
}

export const EnrollDialog = observer(function EnrollDialog({
  app,
  editStudy,
  onClose,
  onSaved,
  onDeleted,
}: {
  app: AppModel;
  editStudy?: ClinicalStudies;
  onClose: () => void;
  onSaved: (studyAcronym: string) => void;
  onDeleted?: () => void;
}) {
  const { t } = useLingui();
  const patient = app.requirePatient();
  const isEdit = !!editStudy;
  const { confirm } = useDialog();

  const [body] = useState<EnrollFormState>(() =>
    observable({
      patient_id: patient.patientId,
      enrollment_date: getServerTodayISO(),
      study_acronym: editStudy,
      comment: undefined,
    }),
  );

  const [prefillSchedule] = useState(() =>
    observable([] as ProposedStudyVisitSchedule[]),
  );
  const [visits] = useState(() => observable([] as VisitData[]));
  const [enrollment, setEnrollment] =
    useState<ClinicalStudyEnrollmentResponse>();
  const [prefillLoading, setPrefillLoading] = useState(false);
  const [originalComment, setOriginalComment] = useState("");
  const [originalVisitDates, setOriginalVisitDates] = useState<
    Record<string, string>
  >({});
  const prefillGenRef = useRef(0);

  const tryFetchPrefill = async () => {
    if (!body.study_acronym || !body.enrollment_date) return;
    const gen = ++prefillGenRef.current;
    try {
      setPrefillLoading(true);
      const { schedule, visits: prefillVisits } = await fetchEnrollmentPrefill(
        body.study_acronym,
        body.enrollment_date,
        requireNonNull(patient.data).country,
      );
      if (gen !== prefillGenRef.current) return;
      prefillSchedule.splice(0, prefillSchedule.length, ...schedule);
      visits.splice(0, visits.length, ...prefillVisits);
    } catch (error) {
      if (gen === prefillGenRef.current) window.handleError(error);
    } finally {
      if (gen === prefillGenRef.current) setPrefillLoading(false);
    }
  };

  const { data: studies, isPending } = useQuery({
    ...apiGetClinicalStudiesOptions({
      query: { patient_ids: [patient.patientId] },
    }),
    enabled: isEdit,
  });
  const loading = isEdit && isPending;

  useEffect(() => {
    if (!studies) return;
    const found = studies.study_enrollments.find(
      (e) => e.study_acronym === editStudy,
    );
    if (found) {
      setEnrollment(found);
      // Show the actual enrollment date rather than today's (field is
      // disabled in edit mode and not sent, but the displayed value matters).
      body.enrollment_date = found.enrollment_date;
      const sorted = found.study_visits
        .slice()
        .sort((a, b) => compareDateStr(a.visit_datetime, b.visit_datetime));
      visits.splice(
        0,
        visits.length,
        ...sorted.map(
          (v): VisitData => ({
            uuid: v.uuid,
            visit_date: dateTimeStrToDateStr(v.visit_datetime),
            visit_type: v.visit_type,
          }),
        ),
      );
      setOriginalComment(found.comment ?? "");
      // Seed the editable field with the existing comment so it's shown to the
      // user and not treated as a dirty change that overwrites it with null.
      body.comment = found.comment ?? "";
      setOriginalVisitDates(
        Object.fromEntries(
          sorted.map((v) => [v.uuid, dateTimeStrToDateStr(v.visit_datetime)]),
        ),
      );
    }
  }, [studies, editStudy, body, visits]);

  const isDirty =
    !isEdit ||
    (body.comment ?? "") !== originalComment ||
    visits.some((v) => v.uuid && originalVisitDates[v.uuid] !== v.visit_date);

  const canSubmit =
    isDirty && (isEdit || (!!body.study_acronym && !!body.enrollment_date));

  async function save() {
    // canSubmit guards this in the UI; narrow for the rest of the function.
    const studyAcronym = body.study_acronym;
    if (!studyAcronym) return;

    try {
      let enrollment0 = enrollment;

      if (!isEdit) {
        const res = await apiPostClinicalStudiesEnroll({
          body: toEnrollPayload(body, studyAcronym, prefillSchedule, visits),
        });
        enrollment0 = res.data;
      }
      if (!enrollment0) return;

      const enrollmentUuid = enrollment0.uuid;
      await Promise.all(
        visits.map(async (s) => {
          if (s.uuid) {
            // Only PUT visits whose date actually changed.
            if (originalVisitDates[s.uuid] === s.visit_date) return;
            await apiPutClinicalStudiesStudyVisitUuid({
              path: { uuid: s.uuid },
              body: { visit_datetime: dateTimeISOString(s.visit_date) },
            });
          } else {
            await apiPostClinicalStudiesStudyVisit({
              body: toVisitPayload(
                s,
                studyAcronym,
                enrollmentUuid,
                patient.patientId,
              ),
            });
          }
        }),
      );

      if (isEdit && (body.comment ?? "") !== originalComment) {
        await apiPutClinicalStudiesEnrollmentUuid({
          path: { uuid: enrollment0.uuid },
          body: { comment: body.comment ?? null },
        });
      }

      if (!isEdit) {
        await patient.fetchEnrollments();
      }

      void queryClient.invalidateQueries({
        queryKey: apiGetClinicalStudiesQueryKey(),
      });
      onSaved(studyAcronym);
    } catch (error) {
      window.handleError(error);
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      width="620px"
      className="max-h-[90dvh]"
    >
      <DialogHeader>
        {isEdit ? t`Edit enrollment` : t`Enroll in new study`}
      </DialogHeader>
      <Form className="flex min-h-0 flex-1 flex-col" onSubmit={() => save()}>
        <DialogBody>
          {loading ? (
            <Spinner />
          ) : (
            <ClinicalStudyEnrollmentFormFields
              enrollmentModel={body}
              visits={visits}
              onStudyChange={tryFetchPrefill}
              onDateChange={tryFetchPrefill}
              prefillLoading={prefillLoading}
              disabled={isEdit}
            />
          )}
        </DialogBody>

        <DialogFooter>
          {isEdit && onDeleted && enrollment && (
            <Button
              type="button"
              destructive
              className="sm:mr-auto"
              onClick={async () => {
                if (
                  await confirm(t`Are you sure?`, undefined, {
                    destructive: true,
                  })
                ) {
                  try {
                    await apiDeleteClinicalStudiesEnrollmentUuid({
                      path: { uuid: enrollment.uuid },
                    });
                    await patient.fetchEnrollments();
                    void queryClient.invalidateQueries({
                      queryKey: apiGetClinicalStudiesQueryKey(),
                    });
                    onDeleted();
                  } catch (error) {
                    window.handleError(error);
                  }
                }
              }}
            >
              <Trans>Delete</Trans>
            </Button>
          )}
          <Button type="button" onClick={onClose}>
            <Trans>Cancel</Trans>
          </Button>
          <Button variant="primary" type="submit" disabled={!canSubmit}>
            {isEdit ? t`Save` : t`Enroll`}
          </Button>
        </DialogFooter>
      </Form>
    </Dialog>
  );
});
